<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { $bus } from '@/common/events'
import { $virtualKeyboard } from '@/common/helper'
import { useVirtualKeyboard } from '@/composables/keyboard'
import KeyboardLayout from '@/components/KeyboardLayout.vue'
import KeyboardMacbook from '@/components/KeyboardMacbook.vue'
import KeyboardNumpad from '@/components/KeyboardNumpad.vue'
// import KeyboardNumpad1 from '@/components/KeyboardNumpad1.vue'

const {
  vkeyboard,
  keyboardType,
  keyboardLayout,
  isKeyboardReady,
  isKeyboardEnabled,
  isKeyboardVisible,
  canRepositionCaret,
  canAutoHideOnEnter,
  activeTarget,
} = useVirtualKeyboard();

const props = defineProps({
  //type: {
  //  type: String,
  //  default: "default", //default (compact), macbook, numeric
  //},
});

const emit = defineEmits([ "keypress", "keydown", "keyup" ]);

defineExpose({ show, hide });

let delayTimer = null;
let clearing = false;
let preventDoubleHide = false;
let keyboardTargetInput = null;

const isLoading = ref(false);

const layoutDefault = ref(null);
const layoutNumeric = ref(null);
const layout = computed(() => layoutDefault.value || layoutNumeric.value);

const initTimer = ref();
const showKeyboard = (data) => show(data.target, data.options);
const hideKeyboard = (data) => hide(data.target, data.options);

onMounted(() => {
  vkeyboard.on("keyboard keep", keep);
  vkeyboard.on("keyboard show", showKeyboard);
  vkeyboard.on("keyboard hide", hideKeyboard);

  //Wait for slide-out animation to complete
  initTimer.value = setTimeout(() => {
    isKeyboardReady.value = true;
  }, 300 + 100);
});

onUnmounted(() => {
  vkeyboard.off("keyboard keep", keep);
  vkeyboard.off("keyboard show", showKeyboard);
  vkeyboard.off("keyboard hide", hideKeyboard);
  clearTimeout(initTimer.value);
  isKeyboardReady.value = false;
});

//Move keyboard
/*
var keyboard = document.getElementById("keyboard");
var isDown = false;
keyboard.addEventListener('mousedown', function(e) {
  isDown = true;
  console.log("mousedown");
}, true);

document.addEventListener('mouseup', function() {
  isDown = false;
  console.log("mouseup");
}, true);

document.addEventListener('mousemove', function(event) {
  event.preventDefault();
  if (isDown) {
    var deltaX = event.movementX;
    var deltaY = event.movementY;
    console.log("mousemove", deltaX, deltaY);
    var rect = keyboard.getBoundingClientRect();
    keyboard.style.left = rect.x + deltaX + 'px';
    keyboard.style.top  = rect.x + deltaX + 'px';
  }
}, true);
*/

/*
let keyboardTargetInput = null;
const isKeyboardVisible = ref(props.visible);
const isKeyboardReady = ref(false);

$bus.on("keyboard", (data) => {
  if (typeof data == "object") {
    //No interaction if keyboard is turned off
    if (!props.enabled) {
      return;
    }

    //Apply value only if specified in arguments and will make actual change
    if (typeof data.visible != "undefined" && isKeyboardVisible.value != !!data.visible) {
      isKeyboardVisible.value = !!data.visible;
      applyVisibility();
    }

    //Focused input element
    keyboardTargetInput = data.input;
  }
});

function applyVisibility() {
  let visible = isKeyboardVisible.value;
  $virtualKeyboard.visible = visible;

  if (visible) {
    $bus.emit("keyboard shown");
  }
  else {
    $bus.emit("keyboard hidden");

    //Unfocus input element
    if (keyboardTargetInput && typeof keyboardTargetInput.blur == "function") {
      keyboardTargetInput.blur();
    }
  }
}

function show() {
  isKeyboardVisible.value = true;
  applyVisibility();
}

function hide() {
  isKeyboardVisible.value = false;
  applyVisibility();
}

function keypress(e) {
  if (!e.char) {
    return;
  }

  let input = keyboardTargetInput;
  if (!input || typeof input.value != "string") {
    return;
  }

  if (e.special) {
    switch (e.char) {
      case ":backspace": //Backspace
        if (input.value.length > 0) {
          input.value = input.value.substring(0, input.value.length - 1);
          input.dispatchEvent(new Event("input", { bubbles: true })); //Apply the change
        }
        break;

      case ":enter": //Enter
        hide();
        break;
    }
  }
  else {
    input.value += e.char;
    input.dispatchEvent(new Event("input", { bubbles: true })); //Apply the change
  }

  emit("keypress", e);
}

function keydown(e) {
  if (!e.char) {
    return;
  }
  emit("keydown", e);
}

function keyup(e) {
  if (!e.char) {
    return;
  }
  emit("keyup", e);
}

watch(props.visible, (value, prev) => {
  applyVisibility();
});
*/


//Keep it open/closed, i.e. cancel the delay timer
function keep() {
  clearing = true;
  if (delayTimer) {
    clearTimeout(delayTimer);
    delayTimer = null;
  }
  clearing = false;
}

function show(target, options) {
  //No interaction if keyboard is turned off
  if (!vkeyboard.enabled) {
    return;
  }

  keep();

  let hasTargetChanged = (keyboardTargetInput != target);

  //Focused input element
  keyboardTargetInput = target;
  vkeyboard.target = target;
  activeTarget.value = target;

  if (hasTargetChanged) {
    vkeyboard.emit("target change", target);
  }

  //Release modifier keys
  if (typeof layout.value?.reset == "function") {
    layout.value.reset();
  }

  //Check if already visible
  if (isKeyboardVisible.value) {
    return;
  }

  //Snapshot scroll to undo the browser's auto-scroll-into-view on focus
  const savedScrollX = window.scrollX;
  const savedScrollY = window.scrollY;

  const action = () => {
    if (clearing) return;
    isKeyboardVisible.value = true;
    vkeyboard.visible = true;
    vkeyboard.emit("keyboard shown");
    requestAnimationFrame(() => window.scrollTo(savedScrollX, savedScrollY));
  };

  //Delay or immediate call
  if (options && options.delay > 0) {
    delayTimer = setTimeout(action, options.delay);
  }
  else {
    action();
  }
}

function hide(target, options) {
  if (preventDoubleHide) {
    return;
  }
  
  //No interaction if keyboard is turned off
  if (!vkeyboard.enabled) {
    return;
  }

  keep();

  activeTarget.value = null;

  //Check if already hidden
  if (!isKeyboardVisible.value) {
    return;
  }

  const action = () => {
    if (clearing) return;

    //props.visible = false;
    isKeyboardVisible.value = false;
    vkeyboard.visible = false;

    //Unfocus input element
    if (keyboardTargetInput && typeof keyboardTargetInput.blur == "function") {
      preventDoubleHide = true;
      keyboardTargetInput.blur();
    }

    //Focused input element
    keyboardTargetInput = null;
    preventDoubleHide = false;

    vkeyboard.emit("target change", null); //No target, component can unfocus
    vkeyboard.emit("keyboard hidden");
  };

  if (options && options.delay > 0) {
    delayTimer = setTimeout(action, options.delay);
  }
  else {
    action();
  }
}

function keypress(e) {
  keep();

  if (!e.char) {
    return;
  }

  let input = keyboardTargetInput;
  if (!input || typeof input.value != "string") {
    return;
  }

  if (e.special) {
    switch (e.char) {
      case ":delete": //Delete
      case ":backspace": //Backspace
        console.log("[keyboard] Backspace");
        if (input.value.length > 0) {
          let supportsSelection = input.selectionStart != null;
          if (canRepositionCaret.value == true && supportsSelection) {
            //Backspace at caret position
            let caretPosition = input.selectionStart;
            let selectionLength = input.selectionEnd - input.selectionStart;
            console.log("[keyboard] Text input selection", "start:", input.selectionStart, "end:", input.selectionEnd, "length:", selectionLength)
            if (caretPosition > 0 || selectionLength > 0) {
              let capture = input.value || "";
              let before = capture.substring(0, caretPosition) || "";
              let after = capture.substring(caretPosition + selectionLength) || "";
              //If no text selected rely on caret position and delete last char; otherwise delete the whole selection (but keep the last char)
              if (selectionLength == 0) {
                let deleteDirection = (e.char == ":backspace" ? -1 : +1); //"Backspace" key to remove prev char, "Delete" key to remove next char
                before = before.substring(0, before.length + deleteDirection);
              }
              input.value = before + after;
              input.selectionStart = before.length;
              input.selectionEnd = input.selectionStart;

              //Apply the change
              input.dispatchEvent(new Event("input", { bubbles: true }));
            }
          }
          else {
            //Backspace at the end
            input.value = input.value.substring(0, input.value.length - 1);

            //Apply the change
            input.dispatchEvent(new Event("input", { bubbles: true }));
          }
        }
        break;

      case ":clear":
        console.log("[keyboard] Clear");
        vkeyboard.emit("clear", e);
        input.value = "";
        break;

      case ":ok":
        console.log("[keyboard] OK");
        vkeyboard.emit("confirm", e);
        hide();
        break;

      case ":enter":
        console.log("[keyboard] Enter");
        vkeyboard.emit("enter", e);
        if (canAutoHideOnEnter.value) {
          console.log("[keyboard] Auto hide on Enter")
          hide();
        }
        break;

      case ":left":
      case ":right":
        if (input.selectionStart != null) {
          let direction = (e.char == ":left" ? -1 : 1);
          let pos = null;
          if (input.selectionStart !== input.selectionEnd)
            pos = (e.char == ":left" ? input.selectionStart : input.selectionEnd)
          else
            pos = Math.max(0, Math.min((input.value || "").length, input.selectionStart + direction));
          input.selectionStart = pos;
          input.selectionEnd = pos;
        }
        break;
    }
  }
  else {
    let supportsSelection = input.selectionStart != null;
    if (canRepositionCaret.value == true && supportsSelection) {
      //Insert at caret position
      let caretPosition = input.selectionStart;
      let selectionLength = input.selectionEnd - input.selectionStart;
      console.log("[keyboard] Text input selection", "start:", input.selectionStart, "end:", input.selectionEnd, "length:", selectionLength)
      let capture = input.value || "";
      let before = capture.substring(0, caretPosition) || "";
      let after = capture.substring(caretPosition + selectionLength) || "";
      input.value = before + e.char + after;
      input.selectionStart = (before + e.char).length;
      input.selectionEnd = input.selectionStart;
    }
    else {
      //Append only
      input.value += e.char;
    }

    //Apply the change
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }
  
  emit("keypress", e);
  vkeyboard.emit("keypress", e);
}

function keydown(e) {
  keep();
  if (!e.char) {
    return;
  }
  emit("keydown", e);
  vkeyboard.emit("keydown", e);
}

function keyup(e) {
  if (!e.char) {
    return;
  }
  emit("keyup", e);
  vkeyboard.emit("keyup", e);
}
</script>

<template>
  <!-- Keyboard container -->
  <div class="keyboard-container" :class="{ 'slide-in': isKeyboardVisible, 'slide-out': !isKeyboardVisible, 'keyboard-ready': isKeyboardReady }" :style="{ 'transform': isLoading ? 'translateY(-10000px)' : 'translateY(0)' }">

    <!-- Panel graphics -->
    <div id="keyboard" class="keyboard-panel relative">

      <!-- Keyboard layout -->
      <KeyboardLayout ref="layoutDefault" class="keyboard-default" @keypress="keypress" @keyup="keyup" @keydown="keydown" @mousedown="keep" v-if="keyboardType == 'default'" />
      <KeyboardNumpad ref="layoutNumeric" class="keyboard-numeric" @keypress2="keypress" @keyup="keyup" @keydown="keydown" @mousedown="keep" v-if="keyboardType == 'numeric'" :display="false" :iconic="false" />

      <!-- Tab/button to hide keyboard -->
      <div class="absolute top-0 right-0 keyboard-hide-tab" style="top: -27px; right: 25px;">
        <button @click="hide" class="px-3 pt-0.5 pb-0"><i class="fa fa-angle-down"></i></button>
      </div>

    </div>

  </div>
</template>

<style scoped>
/* note: compact keyboard height = 300px */

.keyboard-container {
  display: block;
  position: fixed;
  width: 100%;
  left: 0;
  bottom: -450px;
  z-index: 50;
  opacity: 0;
}

.keyboard-ready {
  opacity: 1 !important;
}

.keyboard-container.slide-in {
  animation: slide-in 0.3s forwards;
  /* animation-delay: 2s; */
}

.keyboard-container.slide-out {
  animation: slide-out 0.3s forwards;
  /* animation-delay: 2s; */
}

@keyframes slide-in {
  100% { bottom: -32px; }
  /* 100% { transform: translateX(0%); } */
}

@keyframes slide-out {
  0% { bottom: -32px; }
  100% { bottom: -450px; }
  /* 0% { transform: translateX(0%); } */
  /* 100% { transform: translateX(-100%); } */
}

.keyboard-panel {
  @apply border rounded-lg shadow-xl bg-white
    dark:bg-gray-800 dark:border-gray-500;
  /* width: 1105px; */
  /* width: 772px; */
  /* width: 716px; */
  width: 722px;
  margin: 0 auto;
}

.keyboard-default {
}

.keyboard-numeric {
  @apply p-8 pb-0;
}

.keyboard-hide-tab {
  @apply border-s border-t border-e rounded-t-lg bg-white
    dark:bg-gray-800 dark:border-gray-500 dark:text-white;
}
</style>
